'use client'

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'

import { LoadingOverlay } from '@/app/components/shared/LoadingOverlay'

interface PageLoadingContextValue {
  register: (id: string, label: string) => void
  unregister: (id: string) => void
}

const PageLoadingContext = createContext<PageLoadingContextValue | null>(null)

/** Cuánto se retrasa ocultar el overlay tras quedarse sin motivos activos —
 * un margen corto para que un segundo motivo que arranca casi a la vez (p. ej.
 * "Vistos recientemente" empezando su propio fetch justo cuando termina la
 * redirección de `GeoLocationSync`) se perciba como el mismo spinner
 * continuando, no como uno que se apaga y otro que se enciende después. */
const HIDE_DEBOUNCE_MS = 150

/**
 * Coordina UN único `LoadingOverlay` de pantalla completa entre varios
 * consumidores independientes de la misma página (p. ej. `GeoLocationSync` y
 * `RecentlyViewed`), que se montan/desmontan en momentos distintos y no se
 * conocen entre sí. Sin este punto compartido, cada uno pinta su propio
 * overlay y el visitante percibe un parpadeo (uno se apaga, un instante de
 * contenido real, y otro se enciende) en vez de una sola carga continua.
 *
 * Vive en el layout de la web general (persiste entre navegaciones dentro de
 * ese árbol, incluida la que hace `GeoLocationSync` con `router.replace()`),
 * así que un consumidor que se desmonta al cambiar de página (la página
 * "todos" original) y otro que se monta en la página localizada que la
 * sustituye SÍ pueden solaparse a través de este registro compartido.
 */
export function PageLoadingProvider({ children }: { readonly children: ReactNode }) {
  const [visibleLabel, setVisibleLabel] = useState<string | null>(null)
  // Vive en un ref, no en estado: nada necesita re-renderizar cuando cambia el
  // propio registro — solo `visibleLabel` (derivado de él) le importa al render.
  const reasonsRef = useRef(new Map<string, string>())
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Reconcilia `visibleLabel` con el registro actual. Se llama desde
  // `register`/`unregister` (callbacks normales, no el cuerpo de un efecto),
  // así que el `setState` de la rama "hay motivos activos" es síncrono y
  // legítimo — solo la ocultación necesita un temporizador real.
  const sync = useCallback(() => {
    const reasons = reasonsRef.current
    if (reasons.size > 0) {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current)
        hideTimeoutRef.current = null
      }
      // El más reciente en registrarse (última entrada del `Map`) es el que
      // mejor describe lo que está pasando ahora mismo.
      setVisibleLabel([...reasons.values()].at(-1) ?? null)
      return
    }

    if (hideTimeoutRef.current) return // ya hay uno programado
    hideTimeoutRef.current = setTimeout(() => {
      hideTimeoutRef.current = null
      setVisibleLabel(null)
    }, HIDE_DEBOUNCE_MS)
  }, [])

  const register = useCallback(
    (id: string, label: string) => {
      reasonsRef.current.set(id, label)
      sync()
    },
    [sync],
  )

  const unregister = useCallback(
    (id: string) => {
      if (!reasonsRef.current.delete(id)) return
      sync()
    },
    [sync],
  )

  // Limpia el temporizador pendiente si el propio provider se desmonta.
  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current)
    }
  }, [])

  const value = useMemo(() => ({ register, unregister }), [register, unregister])

  return (
    <PageLoadingContext.Provider value={value}>
      {visibleLabel && <LoadingOverlay label={visibleLabel} />}
      {children}
    </PageLoadingContext.Provider>
  )
}

/**
 * Registra `active`/`label` como un motivo de carga de página mientras
 * `active` es `true`. Fuera de un `PageLoadingProvider` (p. ej. un test que
 * renderiza el consumidor aislado) no falla — simplemente no coordina nada, y
 * el propio consumidor debe pintar su overlay local (ver el valor devuelto).
 */
export function usePageLoadingOverlay(active: boolean, label: string): { readonly standalone: boolean } {
  const ctx = useContext(PageLoadingContext)
  const id = useId()

  useEffect(() => {
    if (!ctx) return undefined
    if (active) {
      ctx.register(id, label)
    } else {
      ctx.unregister(id)
    }
    return () => ctx.unregister(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `ctx` son las funciones estables del provider (memoizadas con useCallback/useMemo); solo `active`/`label` deben re-disparar el registro.
  }, [active, label, id])

  return { standalone: ctx === null }
}
